// Free gift picker.
//
// The card does not post and it does not touch the product form. It says what
// it would add and nextdev-product-form.js does the rest: one listener on the
// form, one request for every extra line, one group tying them to the bed. The
// form itself stays one line, so nextdev-product-form.js does what it always
// does: posts the bed, shows its own error, opens its own cart.
//
// Catching the submit here instead was what this did first, and it cannot be
// done by two snippets at once: see the note at the top of
// nextdev-product-form.js.
//
// Unlike the upsell card there is nothing to tick: a gift is always chosen, so
// a line always goes in. The one exception is every gift being sold out in
// what the bed is set to; then nothing is chosen and the bed goes in alone.
//
// A gift also follows the bed. Where the two products share an option name,
// Size or Color, the gift moves to the variant carrying the same value, so a
// green bed comes with a green blanket. Liquid picks the first one, and this
// moves it afterwards: the section says what is chosen through the
// nextdev:pdp:variant event, and nextdevPdpOptions on its root answers the same
// question on load, whichever of the two scripts started first.
//
// A gift out of stock in that choice is not skipped. It used to be: nothing
// available agreed, so the tile kept the picture and the variant of whatever
// was chosen before, and an XL gray bed came with a beige gift in the picture
// and in the cart. Now the tile shows the gift in the colour the bed is set to,
// is marked sold out, and holds no variant, so nothing it cannot give is sent.
//
// Sold out counts the shelf, not just the product. A gift carrying the bed's
// own SKU is the same thing off the same shelf, and Shopify, keeping the two
// products' stock apart, would sell the last one as the bed and again as the
// gift. With the SKUs the same the shelf is asked for both at once, the bed's
// and the gift's, and a gift it cannot cover is sold out.

(function () {
  'use strict';

  function start(card) {
    if (card.dataset.nextdevGiftInit) return;
    card.dataset.nextdevGiftInit = '1';

    var options = Array.prototype.slice.call(
      card.querySelectorAll('[data-nextdev-pdp-free-gift-option]')
    );
    if (!options.length) return;

    // ---- the gift follows the bed ----

    function dataOf(option) {
      if (option.nextdevGiftData !== undefined) return option.nextdevGiftData;
      var raw = option.getAttribute('data-gifts');
      try {
        option.nextdevGiftData = raw ? JSON.parse(raw) : null;
      } catch (e) {
        option.nextdevGiftData = null;
      }
      return option.nextdevGiftData;
    }

    // The bed's own variants, with their SKU and stock, off the card.
    var bed = null;
    try {
      bed = JSON.parse(card.getAttribute('data-bed') || 'null');
    } catch (e) {
      bed = null;
    }

    function bedVariantFor(picked) {
      if (!bed || !bed.variants || !picked) return null;
      for (var i = 0; i < bed.variants.length; i++) {
        var variant = bed.variants[i];
        var all = true;
        for (var n = 0; n < bed.names.length; n++) {
          if (picked[bed.names[n]] !== undefined && variant.options[n] !== picked[bed.names[n]]) {
            all = false;
            break;
          }
        }
        if (all) return variant;
      }
      return null;
    }

    // What the bed is set to right now, kept by follow() for the tests below.
    var bedNow = null;

    // Whether this variant of a gift can go in beside the bed. A stock of
    // null has no end to it. Sharing the bed's SKU, the two draw on one
    // shelf: the smaller of the two counts is taken as the shelf, since a
    // shop keeping them in step has them equal and one out of step is only
    // safe at the lower, and it has to cover the bed and the gift together.
    function canGive(variant) {
      if (!variant.available) return false;
      var quantity = bedQuantity();
      var stock = variant.stock;
      if (stock === null || stock === undefined) return true;
      if (bedNow && bedNow.sku && variant.sku === bedNow.sku) {
        var shelf = bedNow.stock === null || bedNow.stock === undefined ? stock : Math.min(stock, bedNow.stock);
        return shelf >= quantity * 2;
      }
      return stock >= quantity;
    }

    // Agrees on every option the two products share, and is not asked about
    // the ones only the gift has. A gift sharing nothing agrees always, which
    // leaves it on its first available variant.
    function agrees(data, variant, picked) {
      for (var i = 0; i < data.names.length; i++) {
        var wanted = picked[data.names[i]];
        if (wanted === undefined) continue;
        if (variant.options[i] !== wanted) return false;
      }
      return true;
    }

    // Why a tile did or did not move, written on the tile itself. Following
    // fails three ways and every one of them looks the same from outside: the
    // two products share no option name, nothing available agrees on the
    // value, or the variant it matched carries no picture of its own. None of
    // those is a fault in this file, they are what the shop's own products
    // say, and without this there is no way to tell which one is happening.
    function note(option, why) {
      option.setAttribute('data-follow', why);
    }

    // The first variant the gift can actually be given in, for a tile coming
    // back from sold out with nothing left in its input.
    function firstAvailable(data) {
      for (var i = 0; i < data.variants.length; i++) {
        if (canGive(data.variants[i])) return data.variants[i];
      }
      return null;
    }

    // A srcset beats src. Liquid draws this picture through `image_tag`,
    // which writes one, so an img left with that srcset keeps drawing the
    // picture the page was rendered with however often the src is
    // rewritten: the tile held still while the variant under it moved.
    // A following tile draws from src alone from here on, which a small
    // fixed square loses nothing by. A gift told to hold its picture still
    // is left alone, and so is a variant with no picture of its own.
    function showImage(option, url) {
      if (!url || option.hasAttribute('data-image-fixed')) return;
      var img = option.querySelector('.nextdev--pdp__free-gift-picker-media img');
      if (!img) return;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      if (img.getAttribute('src') !== url) img.setAttribute('src', url);
    }

    // Sold out in what the bed is set to. The tile still shows the gift in
    // that colour, so what is on screen matches the bed, but it cannot be
    // picked and it holds no variant: its input is disabled and emptied, and
    // the cart only ever takes an input's value, so nothing that cannot be
    // given reaches it. Coming back, it takes the variant it was matched to,
    // or, with none, the first it can be given in.
    function setSoldOut(option, soldOut, data, variant) {
      var input = option.querySelector('[data-nextdev-pdp-free-gift-input]');
      var label = option.querySelector('[data-nextdev-pdp-free-gift-soldout]');
      option.classList.toggle('nextdev--pdp__free-gift-picker-option--sold-out', soldOut);
      if (soldOut) option.setAttribute('aria-disabled', 'true');
      else option.removeAttribute('aria-disabled');
      if (label) label.hidden = !soldOut;
      if (!input) return;
      input.disabled = soldOut;
      if (soldOut) {
        input.checked = false;
        input.value = '';
        return;
      }
      if (variant) {
        input.value = variant.id;
      } else if (!input.value) {
        var first = firstAvailable(data);
        if (first) input.value = first.id;
      }
    }

    // With the chosen tile gone sold out, the choice moves to the first one
    // that can still be given. Done once, after every tile has been looked
    // at, so the choice never lands on a tile a later one in the same pass
    // takes out. With every tile sold out nothing is chosen, and the bed goes
    // into the cart alone.
    function keepChoiceAvailable() {
      if (!chosenInput()) {
        for (var i = 0; i < options.length; i++) {
          var input = options[i].querySelector('[data-nextdev-pdp-free-gift-input]');
          if (input && !input.disabled && input.value) {
            input.checked = true;
            break;
          }
        }
      }
      markChosen();
    }

    var lastPicked = null;

    function follow(picked) {
      if (!picked) return;
      lastPicked = picked;
      bedNow = bedVariantFor(picked);
      options.forEach(function (option) {
        var data = dataOf(option);
        if (!data || !data.variants || !data.variants.length) {
          note(option, 'no-data');
          return;
        }

        // whether the bed is being asked about anything this gift carries
        var shares = false;
        for (var n = 0; n < data.names.length; n++) {
          if (picked[data.names[n]] !== undefined) {
            shares = true;
            break;
          }
        }

        // What can be given in that choice, and, failing that, what would be
        // if it were in stock. The second is only there to draw: it is never
        // put in an input.
        var match = null;
        var soldOut = null;
        for (var i = 0; i < data.variants.length; i++) {
          var candidate = data.variants[i];
          if (!agrees(data, candidate, picked)) continue;
          if (canGive(candidate)) {
            match = candidate;
            break;
          }
          if (!soldOut) soldOut = candidate;
        }

        if (match) {
          setSoldOut(option, false, data, match);
          if (option.hasAttribute('data-image-fixed')) {
            note(option, 'held-still');
            return;
          }
          if (!match.image) {
            note(option, 'no-variant-image');
            return;
          }
          showImage(option, match.image);
          note(option, shares ? 'following' : 'no-shared-option');
          return;
        }

        // The gift comes in that choice but it is out of it. Only asked when
        // the two share an option: a gift sharing nothing agrees always, so
        // it is never sold out on the bed's account.
        if (shares && soldOut) {
          setSoldOut(option, true, data, null);
          showImage(option, soldOut.image);
          note(option, 'sold-out');
          return;
        }

        // The gift is not made in that choice at all. It stays on what it
        // can be given in, as it always did, and is not called sold out.
        setSoldOut(option, false, data, null);
        note(option, shares ? 'no-variant-agrees' : 'no-shared-option');
      });
      keepChoiceAvailable();
    }

    // the section says so on every change, and the event bubbles, so this
    // catches it wherever the card was rendered
    document.addEventListener('nextdev:pdp:variant', function (event) {
      if (event.detail) follow(event.detail.options);
    });

    // and on load, in case the section had already run before this did
    var section = card.closest ? card.closest('[data-nextdev-pdp]') : null;
    if (section && typeof section.nextdevPdpOptions === 'function') {
      follow(section.nextdevPdpOptions());
    }

    // ---- the tile reads as a choice ----

    function chosenInput() {
      for (var i = 0; i < options.length; i++) {
        var input = options[i].querySelector('[data-nextdev-pdp-free-gift-input]');
        if (input && input.checked) return input;
      }
      return null;
    }

    function markChosen() {
      options.forEach(function (option) {
        var input = option.querySelector('[data-nextdev-pdp-free-gift-input]');
        option.classList.toggle(
          'nextdev--pdp__free-gift-picker-option--selected',
          !!(input && input.checked)
        );
      });
    }

    options.forEach(function (option) {
      var input = option.querySelector('[data-nextdev-pdp-free-gift-input]');
      if (input) input.addEventListener('change', markChosen);
    });

    // liquid draws the first tile as the chosen one, and this says the same
    // thing again against whatever the browser restored on a back button
    markChosen();

    // ---- the form the gift goes in with ----

    // Named by the section that rendered this, or the product form the page
    // already has.
    function findForm() {
      var named = card.getAttribute('data-form');
      if (named) {
        var byId = document.getElementById(named);
        if (byId) return byId.tagName === 'FORM' ? byId : byId.querySelector('form');
      }
      return document.querySelector('form.nextdev--pdp__form')
        || document.querySelector('nextdev-product-form form')
        || null;
    }

    var form = findForm();
    if (!form) return;

    // ---- what this would add ----

    // The bed's own quantity, so the gift goes in one for one with it. The
    // drawer keeps them in step from there.
    // Asked by the stock test too, which runs on load before the form below
    // is looked up, so it finds the form itself when it has to.
    function bedQuantity() {
      var within = form || findForm();
      var field = within ? within.querySelector('[name="quantity"]') : null;
      var value = field ? parseInt(field.value, 10) : 1;
      return value > 0 ? value : 1;
    }

    if (window.nextdevCartCombine) {
      window.nextdevCartCombine.register(form, function () {
        // asked again at the moment of adding, so a quantity changed since
        // the last colour change is counted against the shelf too
        if (lastPicked) follow(lastPicked);
        var input = chosenInput();
        if (!input || !input.value) return null;
        return { id: input.value, quantity: bedQuantity() };
      });
    }
  }

  function boot() {
    var cards = document.querySelectorAll('[data-nextdev-pdp-free-gift]');
    Array.prototype.forEach.call(cards, start);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // the theme editor draws a section again after a change
  document.addEventListener('shopify:section:load', boot);
})();